import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useParams } from 'react-router-dom';
import { Spinner } from 'reactstrap';
import BZAPIService from '../../services/BZAPIService';
import { VendorMark } from '../plan/projects/ProjectAtoms';
import { IcoChevL, IcoExternal } from '../bz/icons';
import { PageHead, PredictCard, ScoreMeter } from './PredictAtoms';
import {
  cveId,
  formatDateTime,
  formatScore,
  isPromoted,
  safeUrl,
  WATCH_LABEL,
  weaknessLabel
} from './predictWatchlistModel';
import { CLASS_LABEL, WATCH_PATH } from './predictDashboardModel';
import { ageSinceFreeze, cweOf, gapLabel, outcomeFor, timelineSteps } from './predictBugModel';
import { sourceLabel } from './predictSources';

const small = { fontSize: 'var(--fs-sm)' };

const PILL_TONE = { predicted: 'bz-pill--predict', 'retro-matched': 'bz-pill--info' };

const BackLink = () => (
  <Link to={WATCH_PATH} className="bz-row" style={{ ...small, gap: 4, width: 'fit-content' }}>
    <IcoChevL size={13} /> {WATCH_LABEL}
  </Link>
);

const Fact = ({ label, children, note }) => (
  <>
    <dt className="bz-muted">{label}</dt>
    <dd>
      {children}
      {note && (
        <div className="bz-muted" style={small}>
          {note}
        </div>
      )}
    </dd>
  </>
);
Fact.propTypes = { label: PropTypes.string.isRequired, children: PropTypes.node, note: PropTypes.string };

const Cwe = ({ cwe }) => {
  const parsed = cweOf(cwe);
  if (!parsed) return <span className="bz-muted">None recorded</span>;
  return parsed.url ? (
    <a href={parsed.url} target="_blank" rel="noopener noreferrer" className="bz-mono">
      {parsed.id}
    </a>
  ) : (
    <span className="bz-mono">{parsed.id}</span>
  );
};
Cwe.propTypes = { cwe: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) };

const Timeline = ({ bug, outcome }) => {
  const steps = timelineSteps(bug, outcome);
  if (!steps.length) return <div className="pdp-empty">No dates recorded for this bug.</div>;
  return (
    <ol className="pdp-timeline" data-testid="timeline">
      {steps.map((step, index) => {
        const gap = index > 0 && step.at ? gapLabel(steps[index - 1].at, step.at) : null;
        return (
          <li key={step.key} className={`pdp-step--${step.key}`} data-testid={`step-${step.key}`}>
            <div className="bz-row" style={{ gap: 8, flexWrap: 'wrap' }}>
              <b style={{ fontWeight: 600 }}>{step.label}</b>
              {step.key === 'cve' && outcome && CLASS_LABEL[outcome.class] && (
                <span className={`bz-pill ${PILL_TONE[outcome.class]}`}>{CLASS_LABEL[outcome.class]}</span>
              )}
            </div>
            <div className="bz-mono" style={small}>
              {step.at ? formatDateTime(step.at) : 'Date not recorded'}
            </div>
            <div className="bz-muted" style={small}>
              {[step.note, gap].filter(Boolean).join(' · ')}
            </div>
          </li>
        );
      })}
    </ol>
  );
};
Timeline.propTypes = {
  bug: PropTypes.object.isRequired,
  outcome: PropTypes.shape({ class: PropTypes.string, cve: PropTypes.string, firstPublicAt: PropTypes.string })
};

// The page's address, for sharing. The browser may refuse the clipboard; then
// the address bar still has it.
const CopyLink = () => {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);
  const copy = () =>
    navigator.clipboard
      ?.writeText(window.location.href)
      .then(() => setCopied(true))
      .catch(error => console.error('Could not copy the link:', error));
  return (
    <button type="button" className="bz-btn bz-btn--sm" onClick={copy} aria-live="polite">
      {copied ? 'Link copied' : 'Copy link'}
    </button>
  );
};

/**
 * Predict → a bug's own page (STRY0039090), at /predict/watch/:vendor/:bugId.
 *
 * One bug of the customer's watch list, from GET /predict/bug: its score
 * against 6.0 and the customer's threshold (GET /predict/threshold), the
 * rationale, weakness type and CWE, where it was found, which scorer version
 * scored it, and its timeline from filing to the freeze, and to its CVE when
 * it has one (with the date and class from GET /predict/outcomes). The address
 * can be shared: anyone who can see the bug on their list can open it. A bug
 * that isn't on the caller's list now (the API's 404) gets its own state.
 *
 * Real data only; the route sits behind <ProductGuard product="predict">.
 */
const PredictBug = () => {
  const { vendor, bugId } = useParams();
  const [detail, setDetail] = useState({ status: 'loading', bug: null });
  const [threshold, setThreshold] = useState(null);
  const [outcome, setOutcome] = useState(null);
  const loadId = useRef(0);

  const load = useCallback(() => {
    const id = ++loadId.current;
    const current = () => id === loadId.current;
    setDetail({ status: 'loading', bug: null });
    setOutcome(null);

    BZAPIService.getPredictBug(vendor, bugId)
      .then(response => {
        if (!current()) return;
        const bug = response?.bug || null;
        setDetail(bug ? { status: 'ready', bug } : { status: 'gone', bug: null });
        if (bug && cveId(bug.cve)) {
          BZAPIService.getPredictOutcomes()
            .then(body => {
              if (current()) setOutcome(outcomeFor(body?.outcomes, bug.vendor, bug.bugId));
            })
            .catch(error => console.error('Failed to load the Predict outcomes:', error));
        }
      })
      .catch(error => {
        console.error('Failed to load the Predict bug:', error);
        if (current()) setDetail({ status: 'failed', bug: null });
      });

    BZAPIService.getPredictThreshold()
      .then(body => {
        if (current()) setThreshold(typeof body?.threshold === 'number' ? body.threshold : null);
      })
      .catch(error => console.error('Failed to load the Predict threshold:', error));
  }, [vendor, bugId]);

  useEffect(() => {
    load();
  }, [load]);

  if (detail.status === 'loading') {
    return (
      <div className="pdp-page pdp-page--narrow">
        <BackLink />
        <div className="text-center" style={{ padding: 40 }} role="status">
          <Spinner color="primary" />
          <div className="bz-muted" style={{ marginTop: 8 }}>
            Loading the bug…
          </div>
        </div>
      </div>
    );
  }

  if (detail.status !== 'ready') {
    const gone = detail.status === 'gone';
    return (
      <div className="pdp-page pdp-page--narrow">
        <BackLink />
        <PageHead
          kicker={
            <span className="bz-mono" style={{ textTransform: 'none', letterSpacing: 0 }}>
              {vendor} · {bugId}
            </span>
          }
          title={gone ? 'Not on your watch list' : 'Couldn’t load this bug'}
        />
        <div className="pl-empty" data-testid={gone ? 'bug-gone' : 'bug-failed'}>
          {gone ? (
            <>
              <div className="pl-empty__title">This bug isn&apos;t on your watch list now.</div>
              <div className="bz-muted" style={{ maxWidth: '60ch' }}>
                It may score below your threshold, its project may no longer be in your scope, or the link may be wrong.
                Your watch list has every bug you can open.
              </div>
              <Link to={WATCH_PATH} className="bz-btn bz-btn--sm bz-btn--primary">
                Open the watch list
              </Link>
            </>
          ) : (
            <>
              <div className="pl-empty__title">Predict data is unavailable right now.</div>
              <div className="bz-muted">Try again in a minute.</div>
              <button type="button" className="bz-btn bz-btn--sm" onClick={load}>
                Try again
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  const { bug } = detail;
  const promoted = isPromoted(bug);
  const href = safeUrl(bug.url);
  const cve = cveId(bug.cve);
  const age = ageSinceFreeze(bug.frozenAt);

  return (
    <div className="pdp-page pdp-page--narrow">
      <BackLink />
      <div className="bz-row" style={{ gap: 14, alignItems: 'flex-start' }}>
        <span className="pdp-wide-only">
          <VendorMark vendorId={bug.vendor} label={bug.targetName || bug.vendor} size={40} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <PageHead
            kicker={
              <>
                {bug.targetName || bug.vendor} ·{' '}
                <span className="bz-mono" style={{ textTransform: 'none', letterSpacing: 0 }}>
                  {bug.bugId}
                </span>
              </>
            }
            title={bug.title || bug.bugId}
          >
            <CopyLink />
            {href && (
              <a href={href} target="_blank" rel="noopener noreferrer" className="bz-btn bz-btn--sm bz-btn--primary">
                <IcoExternal size={13} /> Open the original bug
              </a>
            )}
          </PageHead>
        </div>
      </div>

      <div
        className="bz-card"
        role="region"
        aria-label="Score"
        style={{ padding: '16px 20px', display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center' }}
      >
        <div>
          <div className="bz-kicker">Score</div>
          <div className="bz-row" style={{ alignItems: 'baseline', gap: 4 }}>
            <span className="bz-mono" data-testid="bug-score" style={{ fontSize: 44, fontWeight: 600, lineHeight: 1 }}>
              {formatScore(bug.score)}
            </span>
            <span className="bz-muted bz-mono">/ 10</span>
          </div>
        </div>
        <div style={{ flex: '1 1 260px', minWidth: 0 }}>
          <div className="bz-row" style={{ gap: 6, flexWrap: 'wrap' }}>
            {promoted ? (
              <span className="bz-pill bz-pill--predict">
                <span className="bz-dot" />
                Promoted
              </span>
            ) : (
              <span className="bz-pill">Not promoted · shown by your threshold ({formatScore(threshold)})</span>
            )}
            {cve && <span className="bz-pill bz-pill--bad bz-mono">Became {cve}</span>}
          </div>
          <ScoreMeter score={bug.score} threshold={threshold} />
        </div>
      </div>

      <div className="pdp-split">
        <div className="pdp-rail">
          <PredictCard title="Rationale" labelledBy="pdp-rationale">
            <p
              className="pdp-card-body"
              style={{ margin: 0, fontSize: 'var(--fs-md)', lineHeight: 1.6, whiteSpace: 'pre-line' }}
            >
              {bug.reason || '—'}
            </p>
          </PredictCard>
          <PredictCard title="Facts" labelledBy="pdp-facts">
            <div className="pdp-card-body">
              <dl className="pdp-facts">
                <Fact label="Weakness type" note="The scorer's defect class">
                  {weaknessLabel(bug.weaknessType)}
                </Fact>
                <Fact label="CWE">
                  <Cwe cwe={bug.cwe} />
                </Fact>
                <Fact label="Source" note="Where the bug was filed">
                  {sourceLabel(bug.source)}
                </Fact>
                <Fact label="OSS">{bug.targetName || '—'}</Fact>
                <Fact label="Scorer version">
                  <span className="bz-mono">{bug.scorerVersion || '—'}</span>
                </Fact>
                <Fact label="Original bug">
                  {href ? (
                    <a href={href} target="_blank" rel="noopener noreferrer" className="bz-mono" style={small}>
                      {href}
                    </a>
                  ) : (
                    '—'
                  )}
                </Fact>
              </dl>
            </div>
          </PredictCard>
        </div>

        <PredictCard
          title="Timeline"
          labelledBy="pdp-timeline"
          right={
            age === '—' ? null : (
              <span className="bz-muted" style={small} data-testid="freeze-age">
                {age} since the freeze
              </span>
            )
          }
        >
          <div className="pdp-card-body">
            <Timeline bug={bug} outcome={outcome} />
          </div>
        </PredictCard>
      </div>
    </div>
  );
};

export default PredictBug;
